import React, { useContext, useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import _isEmpty from 'lodash/isEmpty';
import { Box, Checkbox, FormControlLabel, makeStyles, TextareaAutosize, Typography } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { Button, colors } from '@lifesize/clients.mui-components';
import { DefaultSettings, MainContext, radioButtonValues } from 'context';
import SearchMeetings from 'components/searchMeetings';
import SearchRoomSystems from 'components/searchRooms';
import CallMeDirectly from 'components/CallMeDirectly';
import CircularProgress from 'components/CircularProgress';
import MeetingTypeForm from 'components/MeetingTypeForm';
import { lsGet, LsKey, lsSet } from 'utils/localStorageUtils';
import { resetSnackBar, setSnackbarMessage } from 'redux/snackBarSlice';
import SectionLabel from 'components/FormLabel';
import { theme } from 'theme';

const TEXT_AREA_CHARACTER_LIMIT = 250;

const Settings = () => {
  const classes = useSettingsStyles();
  const {
    canSchedule,
    existingMeeting,
    oneTimeName,
    radioValue,
    roomSystem,
    setRoomSystem,
    setExistingMeeting,
    setOneTimeName,
    setRadioValue
  } = useContext(MainContext);

  const defaultSettings: DefaultSettings = JSON.parse(lsGet(LsKey.Settings) || '{}');
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [customMessage, setCustomMessage] = useState(defaultSettings.customMessage || '');
  const [includeMessage, setIncludeMessage] = useState(defaultSettings.includeMessage || false);
  const dispatch = useDispatch();
  const getMeetingValue = () => {
    if (radioValue === radioButtonValues.direct) return oneTimeName;
    if (radioValue === radioButtonValues.find) return existingMeeting;
    if (radioValue === radioButtonValues.findRoom) return roomSystem;
  };

  const saveChanges = () => {
    dispatch(resetSnackBar());
    setIsSavingChanges(true);
    setRadioValue && setRadioValue(radioValue as any);
    lsSet(
      LsKey.Settings,
      JSON.stringify({
        meetingType: radioValue,
        customMessage: customMessage,
        includeMessage: includeMessage,
        meetingValue: getMeetingValue()
      })
    );
    setTimeout(() => {
      dispatch(setSnackbarMessage(intl.get('settingsSaved')));
      setIsSavingChanges(false);
    }, 500);
  };

  // Read settings from localstorage and update context with it.
  useEffect(() => {
    // TS hack to avoid keep checking setRadioValue before calling it.
    if (!setRadioValue) return;

    if (_isEmpty(defaultSettings)) {
      setRadioValue(radioButtonValues.direct);
    }

    if (!_isEmpty(defaultSettings)) {
      setRadioValue(defaultSettings.meetingType);
    }

    if (setExistingMeeting && defaultSettings.meetingType === radioButtonValues.find) {
      setExistingMeeting(defaultSettings.meetingValue);
    }

    if (setRoomSystem && defaultSettings.meetingType === radioButtonValues.findRoom) {
      setRoomSystem(defaultSettings.meetingValue);
    }

    if (setOneTimeName && defaultSettings.meetingType === radioButtonValues.direct) {
      setOneTimeName(defaultSettings.meetingValue as string);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Box pb={3}>
      <Typography data-testid="settingsHeader" variant={'h1'}>
        {intl.get('settings')}
      </Typography>
      <Box pt={1} pb={4}>
        <Typography data-testid="settingsSubTitle" variant={'subtitle2'}>
          {intl.get('settingsSubtitle')}
        </Typography>
      </Box>
      <Box data-testid="quickInviteDetails" className={classes.settingsDetails}>
        <SectionLabel label={intl.get('defaultQuickInvite')} />
        <Box pl={1} pt={2}>
          <MeetingTypeForm showOTM />
          {radioValue === radioButtonValues.direct && (
            <Box pl={1}>
              <CallMeDirectly />
            </Box>
          )}
          {radioValue === radioButtonValues.find && (
            <Box pl={1} key={'meetingSearch'}>
              <SearchMeetings />
            </Box>
          )}
          {radioValue === radioButtonValues.findRoom && (
            <Box pl={1} key={'roomSearch'}>
              <SearchRoomSystems />
            </Box>
          )}
        </Box>
        <Box data-testid="customMessageSection" pt={4}>
          <SectionLabel label={intl.get('customMessage')} />
          <Box pl={2}>
            <FormControlLabel
              data-testid="checkBoxSection"
              checked={includeMessage}
              onChange={(event: React.ChangeEvent<{}>, checked: boolean) => setIncludeMessage(checked)}
              control={<Checkbox />}
              label={intl.get('useCustomMessage')}
            />
            <TextareaAutosize
              data-testid="customMessageInput"
              className={classes.textArea}
              value={customMessage}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setCustomMessage(event.target.value)}
              rows={4}
              maxLength={TEXT_AREA_CHARACTER_LIMIT}
            />
            <Typography
              data-testid="charactersLeftCount"
              variant="subtitle1"
              className={customMessage.length === TEXT_AREA_CHARACTER_LIMIT ? classes.textAreaError : ''}
            >
              {`${TEXT_AREA_CHARACTER_LIMIT - customMessage.length} ${intl.get('charactersLeft')}`}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box mt={3} className={classes.button}>
        <Button
          data-testid="saveChangesBtn"
          color="primary"
          variant="contained"
          disabled={(!canSchedule && radioValue !== radioButtonValues.oneTime) || isSavingChanges}
          onClick={saveChanges}
        >
          {intl.get('saveChanges')}
          {isSavingChanges && <CircularProgress />}
        </Button>
      </Box>
    </Box>
  );
};

const useSettingsStyles = makeStyles(() => ({
  settingsDetails: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  textArea: {
    outline: 'none',
    resize: 'none',
    padding: '9px 0 9px 8px',
    maxWidth: '500px',
    width: '100%',
    borderColor: colors.stone,
    borderWidth: '2px',
    borderRadius: 0,
    fontSize: '1.4rem',
    fontFamily: 'azo-sans-web',
    '&:hover': {
      borderColor: colors.lifesize
    },
    '& + h6': {
      textAlign: 'right',
      fontSize: '1.2rem'
    }
  },
  textAreaError: {
    color: colors.darkPepper
  },
  button: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    paddingLeft: theme.spacing(2)
  },
  formLabel: {
    fontWeight: 600,
    fontSize: '1.5rem'
  }
}));

export default Settings;
